/**
 * Axios instance with Sanctum SPA cookie auth.
 *
 * - withCredentials: send cookies on every request
 * - withXSRFToken: axios automatically reads XSRF-TOKEN cookie and
 *   sends it back as X-XSRF-TOKEN header (Sanctum requirement)
 * - 401 interceptor: redirects to /login on authentication failure
 */

import axios, { AxiosError } from 'axios';

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let csrfFetched = false;

/**
 * Sanctum SPA flow: GET /sanctum/csrf-cookie BEFORE the first
 * mutating call so XSRF-TOKEN cookie is in place.
 */
export async function ensureCsrf(): Promise<void> {
  if (csrfFetched) return;
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true });
  csrfFetched = true;
}

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    // 419 (CSRF mismatch) — refetch the cookie and retry once
    if (err.response?.status === 419 && err.config && !(err.config as any)._csrfRetry) {
      csrfFetched = false;
      (err.config as any)._csrfRetry = true;
      return ensureCsrf().then(() => api.request(err.config!));
    }
    // 401 (unauthenticated) — redirect to /login unless already there
    if (err.response?.status === 401) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${next}`;
      }
    }
    return Promise.reject(err);
  },
);

/** Format an axios error into a UX-friendly string. */
export function apiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as any;
    if (data?.message) return String(data.message);
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && first[0]) return String(first[0]);
    }
    return err.message || 'Request failed';
  }
  return err instanceof Error ? err.message : 'Unknown error';
}
